import { useRef, useEffect, useCallback, useState } from 'react';

const DEBOUNCE_MS = 800;

/**
 * Universal auto-save hook with debounced backend persistence + localStorage fallback.
 *
 * @param {string} storageKey   - Unique key for localStorage (e.g. `sp_story_abc123`)
 * @param {object} data         - Current data object to save
 * @param {function} saveFn     - Async function that saves to backend: (data) => Promise
 * @param {object} options
 *   - enabled {boolean}  - Whether auto-save is active (default true)
 *   - debounceMs {number} - Debounce delay (default 800)
 *   - onSaveSuccess {function} - Callback after successful backend save
 *   - onSaveError {function}   - Callback on backend save failure
 *
 * @returns {{ saveStatus, flush, lastSavedAt }}
 *   - saveStatus: 'idle' | 'saving' | 'saved' | 'error' | 'offline'
 *   - flush: manually trigger an immediate save
 *   - lastSavedAt: Date of last successful save
 */
export function useAutoSave(storageKey, data, saveFn, options = {}) {
  const { enabled = true, debounceMs = DEBOUNCE_MS, onSaveSuccess, onSaveError } = options;

  const [saveStatus, setSaveStatus] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const dataRef = useRef(data);
  const savedDataRef = useRef(null);
  const timerRef = useRef(null);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);

  // Keep dataRef fresh
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Core save logic
  const doSave = useCallback(async (dataToSave) => {
    if (savingRef.current) return;
    if (!dataToSave || (typeof dataToSave === 'object' && Object.keys(dataToSave).length === 0)) return;

    // Skip if nothing changed from last save
    const serialized = JSON.stringify(dataToSave);
    if (savedDataRef.current === serialized) {
      if (mountedRef.current) setSaveStatus('saved');
      return;
    }

    savingRef.current = true;
    if (mountedRef.current) setSaveStatus('saving');

    // Always write to localStorage first (instant, survives crash)
    try {
      localStorage.setItem(storageKey, JSON.stringify({ data: dataToSave, ts: Date.now() }));
    } catch (e) { /* quota exceeded - non-fatal */ }

    // Then try backend
    try {
      await saveFn(dataToSave);
      savedDataRef.current = serialized;
      if (mountedRef.current) {
        setSaveStatus('saved');
        setLastSavedAt(new Date());
      }
      onSaveSuccess?.();
      // Clear localStorage on successful backend save
      try { localStorage.removeItem(storageKey); } catch (e) {}
    } catch (err) {
      if (mountedRef.current) setSaveStatus('offline');
      onSaveError?.(err);
    } finally {
      savingRef.current = false;
    }
  }, [storageKey, saveFn, onSaveSuccess, onSaveError]);

  // Debounced save on data change
  useEffect(() => {
    if (!enabled) return;

    // Don't trigger on initial mount before data has actually been loaded
    if (savedDataRef.current === null) {
      savedDataRef.current = JSON.stringify(data);
      return;
    }

    // Skip if data hasn't changed
    const serialized = JSON.stringify(data);
    if (savedDataRef.current === serialized) return;

    if (mountedRef.current) setSaveStatus('idle');

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      doSave(data);
    }, debounceMs);

    return () => clearTimeout(timerRef.current);
  }, [data, enabled, debounceMs, doSave]);

  // Flush on tab hide / sleep
  useEffect(() => {
    if (!enabled) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        clearTimeout(timerRef.current);
        const current = dataRef.current;
        const serialized = JSON.stringify(current);
        if (savedDataRef.current !== serialized) {
          // Sync localStorage write (guaranteed before sleep)
          try {
            localStorage.setItem(storageKey, JSON.stringify({ data: current, ts: Date.now() }));
          } catch (e) {}
          // Fire-and-forget backend save
          saveFn(current).then(() => {
            savedDataRef.current = serialized;
            try { localStorage.removeItem(storageKey); } catch (e) {}
          }).catch(() => {});
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [enabled, storageKey, saveFn]);

  // Flush on page unload
  useEffect(() => {
    if (!enabled) return;
    const handleUnload = () => {
      clearTimeout(timerRef.current);
      const current = dataRef.current;
      const serialized = JSON.stringify(current);
      if (savedDataRef.current !== serialized) {
        try {
          localStorage.setItem(storageKey, JSON.stringify({ data: current, ts: Date.now() }));
        } catch (e) {}
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [enabled, storageKey]);

  // Manual flush
  const flush = useCallback(() => {
    clearTimeout(timerRef.current);
    return doSave(dataRef.current);
  }, [doSave]);

  return { saveStatus, flush, lastSavedAt };
}

/**
 * Load draft from localStorage if it exists and is newer than backend data.
 * Returns the merged data or null if no local draft exists.
 */
export function loadLocalDraft(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (!data || !ts) return null;
    return { data, ts };
  } catch {
    return null;
  }
}

/**
 * Clear a local draft after successful backend restore.
 */
export function clearLocalDraft(storageKey) {
  try { localStorage.removeItem(storageKey); } catch (e) {}
}
